import { z } from "zod"
import {
  branchIdSchema,
  identityIdSchema,
  membershipIdSchema,
  tenantIdSchema,
} from "./identifiers.js"

export const membershipPersonaV1Schema = z.enum([
  "administrator",
  "dispatcher",
  "office_staff",
  "accountant",
  "field_worker",
  "contractor",
  "customer_contact",
])

export const membershipStatusV1Schema = z.enum(["active", "disabled"])

export const capabilityKeyV1Schema = z
  .string()
  .regex(/^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/, "Capability key is invalid.")
  .max(128)

export const monetaryLimitV1Schema = z
  .object({
    key: capabilityKeyV1Schema,
    currency: z.string().regex(/^[A-Z]{3}$/, "Currency must be an ISO 4217 code."),
    amountMinor: z.number().int().nonnegative().safe(),
  })
  .strict()

const unique = (values: readonly string[]): boolean => new Set(values).size === values.length

export const membershipV1Schema = z
  .object({
    version: z.literal(1),
    membershipId: membershipIdSchema,
    identityId: identityIdSchema,
    tenantId: tenantIdSchema,
    persona: membershipPersonaV1Schema,
    capabilities: z.array(capabilityKeyV1Schema).max(256),
    branchGrants: z.array(branchIdSchema).max(512),
    monetaryLimits: z.array(monetaryLimitV1Schema).max(128),
    status: membershipStatusV1Schema,
  })
  .strict()
  .superRefine((membership, context) => {
    if (!unique(membership.capabilities)) {
      context.addIssue({ code: "custom", path: ["capabilities"], message: "Capabilities must be unique." })
    }
    if (!unique(membership.branchGrants)) {
      context.addIssue({ code: "custom", path: ["branchGrants"], message: "Branch grants must be unique." })
    }
    if (!unique(membership.monetaryLimits.map((limit) => `${limit.key}:${limit.currency}`))) {
      context.addIssue({ code: "custom", path: ["monetaryLimits"], message: "Monetary limits must be unique by key and currency." })
    }
  })

export type MembershipPersonaV1 = z.infer<typeof membershipPersonaV1Schema>
export type MembershipStatusV1 = z.infer<typeof membershipStatusV1Schema>
export type MonetaryLimitV1 = z.infer<typeof monetaryLimitV1Schema>
export type MembershipV1 = z.infer<typeof membershipV1Schema>
